import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, ChevronRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const ease = [0.16, 1, 0.3, 1];
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6, ease, delay },
});
const stagger = (i) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-30px" },
  transition: { duration: 0.55, ease, delay: i * 0.09 },
});

const PRICING = [
  { name: "Starter", price: "₹19,999", originalPrice: "₹24,999", period: "/mo", desc: "Get started with strategic applications.", features: ["40 applications / day", "Basic resume review", "Application tracking sheet", "Email support"], popular: false, testid: "pricing-starter", dailyApps: 40, progress: 50 },
  { name: "Pro", price: "₹24,999", originalPrice: "₹34,999", period: "/mo", desc: "Full support for serious candidates.", features: ["40 applications / day", "Full resume & LinkedIn optimization", "Cover letter support", "Weekly strategy call", "Priority queue", "Job tracking dashboard"], popular: true, testid: "pricing-strategic", dailyApps: 40, progress: 67 },
  { name: "Priority", price: "₹39,999", originalPrice: "₹54,999", period: "/mo", desc: "Maximum attention, highest-touch service.", features: ["Unlimited applications / day", "Everything in Pro", "Priority 24hr support", "Bi-weekly strategy sessions", "Custom per-company materials"], popular: false, testid: "pricing-elite", dailyApps: null, progress: 100 },
];

const FAQ_ITEMS = [
  { q: "How do you apply on my behalf?", a: "After our onboarding, we identify relevant openings and submit through official company portals — no automation shortcuts or mass-apply tools." },
  { q: "Do you guarantee job offers or interviews?", a: "No. We don't guarantee offers or interview calls. Our role is to improve execution quality and save your time — hiring decisions are outside our control." },
  { q: "What is your refund policy?", a: "If you're not satisfied with the quality of our strategy or execution, reach out to hello@applystrat.com and we'll discuss it directly. We believe in honest, fair resolutions." },
  { q: "How many applications per day?", a: "Starter and Pro: up to 40/day. Priority: unlimited. Every application is quality-reviewed before submission, not blindly pushed out." },
];

export default function CTASection() {
  return (
    <>
      {/* ── PRICING ── */}
      <section className="py-28 md:py-36 bg-white" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp()} className="text-center max-w-2xl mx-auto mb-16">
            <p className="section-label-orange mb-3">Pricing</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-ink mb-5">Simple, Transparent Pricing</h2>
            <p className="text-lg text-ink-secondary">No hidden fees. No long-term contracts. Cancel anytime.</p>
          </motion.div>
          <TooltipProvider>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-7 max-w-5xl mx-auto items-center">
              {PRICING.map((plan, i) => (
                <motion.div key={plan.name} {...stagger(i)} data-testid={plan.testid}
                  className={`relative rounded-3xl p-10 flex flex-col ${plan.popular ? "glow-card shadow-[0_20px_40px_rgba(255,82,0,0.15)] scale-105 z-10" : "bg-surface-raised hover:-translate-y-1 transition-transform duration-400"}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="px-4 py-1 rounded-full bg-brand text-white text-xs font-medium uppercase tracking-wider">Most Popular</span>
                    </div>
                  )}
                  <h3 className="text-ink font-medium text-xl mb-1">{plan.name}</h3>
                  <p className="text-ink-secondary text-xs mb-5">{plan.desc}</p>
                  <div className="flex items-end gap-2 mb-5">
                    <span className="text-ink-secondary text-base line-through">{plan.originalPrice}</span>
                    <span className="text-4xl font-medium text-ink">{plan.price}</span>
                    <span className="text-ink-secondary text-sm mb-1">{plan.period}</span>
                  </div>
                  <div className="mb-6 p-3 rounded-xl bg-black/[0.03]">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-ink-secondary text-xs">Applications / day</span>
                      <span className="text-brand font-medium text-sm">{plan.dailyApps ? plan.dailyApps : "∞"}</span>
                    </div>
                    <Progress value={plan.progress} className="h-1.5 bg-black/[0.06] [&>div]:bg-brand" />
                  </div>
                  <ul className="space-y-3 mb-8 flex-1">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-center gap-2.5">
                        <CheckCircle2 size={15} className="text-brand flex-shrink-0" />
                        <span className="text-ink-secondary text-sm">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button asChild variant={plan.popular ? "primary" : "outline"} className="w-full" data-testid={`${plan.testid}-cta`}>
                    <Link to="/apply">Apply Now</Link>
                  </Button>
                </motion.div>
              ))}
            </div>
          </TooltipProvider>
          <motion.p {...fadeUp(0.3)} className="text-center text-ink-secondary text-xs mt-8">
            Questions? <a href="mailto:hello@applystrat.com" className="text-brand hover:text-brand-hover transition-colors">hello@applystrat.com</a>
          </motion.p>
        </div>
      </section>

      {/* ── FAQ PREVIEW ── */}
      <section className="py-28 md:py-36 bg-white" data-testid="faq-preview-section">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div {...fadeUp()} className="text-center mb-14">
            <p className="section-label-orange mb-3">FAQ</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-ink">Common Questions</h2>
          </motion.div>
          <motion.div {...fadeUp(0.1)}>
            <Accordion type="single" collapsible className="space-y-3">
              {FAQ_ITEMS.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`} className="bg-surface-raised border-0 rounded-2xl px-6 overflow-hidden hover:bg-white hover:shadow-sm transition-all duration-300">
                  <AccordionTrigger className="text-left text-ink font-medium text-sm py-5 hover:no-underline hover:text-brand transition-colors">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-ink-secondary text-sm leading-relaxed pb-5">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
          <motion.div {...fadeUp(0.2)} className="text-center mt-10">
            <Link to="/faq" className="inline-flex items-center gap-1.5 text-brand hover:text-brand-hover text-sm font-medium transition-colors">
              See all questions <ChevronRight size={15} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="py-28 md:py-40 bg-ink text-white relative overflow-hidden" data-testid="final-cta-section">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-[-80px] left-[10%] w-[420px] h-[420px] bg-brand/[0.14] rounded-full blur-[60px] animate-orb" />
          <div className="absolute bottom-[-100px] right-[8%] w-[360px] h-[360px] bg-amber-400/[0.10] rounded-full blur-[60px] animate-orb-slow" />
        </div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <motion.div {...fadeUp()}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 text-white text-xs font-medium uppercase tracking-widest mb-10">
              <Sparkles size={12} className="text-brand" />
              Limited Slots Available
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white mb-6 leading-tight">
              Let Us Handle the Applications.<br />
              <span className="text-brand">You Focus on Cracking the Interviews.</span>
            </h2>
            <p className="text-lg text-white/60 max-w-xl mx-auto mb-12 leading-relaxed">
              Every slot is carefully managed to ensure each candidate gets serious, focused attention. Apply now to see if you qualify.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild size="lg" className="shadow-lg hover:scale-[1.02]">
                <Link to="/apply" data-testid="final-cta-apply-button">
                  Apply to Work With Us <ArrowRight size={18} />
                </Link>
              </Button>
              <a href="https://cal.com" target="_blank" rel="noopener noreferrer" data-testid="final-cta-book-button" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white/10 text-white font-medium hover:bg-white/20 transition-all duration-300 text-base border border-white/20">
                Schedule a Call
              </a>
            </div>
            <p className="text-white/40 text-xs mt-10">Questions? <a href="mailto:hello@applystrat.com" className="text-brand hover:text-[#FF9A5C] transition-colors">hello@applystrat.com</a></p>
          </motion.div>
        </div>
      </section>
    </>
  );
}
